/**
 * Frontend global error reporter → POST /api/incidents/client-error
 *
 * Non-blocking, fire-and-forget, deduped on the client (same error fires only
 * once per minute so a bug in a render loop doesn't flood the backend).
 *
 * Captures:
 *   - window.onerror (uncaught sync errors)
 *   - window.addEventListener("unhandledrejection") (rejected promises)
 */
const RECENT = new Map();
const DEDUPE_MS = 60_000;

function dedupe(key) {
  const now = Date.now();
  const last = RECENT.get(key);
  if (last && now - last < DEDUPE_MS) return true;
  RECENT.set(key, now);
  // Trim the map so it doesn't grow forever
  if (RECENT.size > 200) {
    const oldest = [...RECENT.entries()].sort((a, b) => a[1] - b[1])[0][0];
    RECENT.delete(oldest);
  }
  return false;
}

async function report({ message, source, lineno, colno, stack }) {
  const key = `${message}::${source}::${lineno}`;
  if (dedupe(key)) return;
  const body = {
    message: String(message || "Unknown error").slice(0, 500),
    source: String(source || "").slice(0, 300),
    lineno: Number(lineno) || 0,
    colno: Number(colno) || 0,
    stack: String(stack || "").slice(0, 4000),
    page_url: typeof window !== "undefined" ? window.location.href : "",
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
  };
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    await fetch(`${base}/api/incidents/client-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    });
  } catch {
    // Intentionally swallow — never let the reporter itself surface errors.
  }
}

export function installErrorReporter() {
  if (typeof window === "undefined") return;
  if (window.__mydawaReporterInstalled) return;
  window.__mydawaReporterInstalled = true;

  window.addEventListener("error", (event) => {
    report({
      message: event.message,
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error && event.error.stack,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason || {};
    report({
      message: reason.message || String(reason) || "Unhandled promise rejection",
      source: "promise",
      stack: reason.stack || "",
    });
  });
}
